import React, { useEffect, useMemo, useState } from "react";
import Ticket from "../components/Ticket";
import { touchItems } from "../constants/const";
import ThreeDCarousel from "../components/ThreeDCarousel";
import { useTonAddress } from "@tonconnect/ui-react";
import {
    getPlayerTicketsInLevel,
    getCycle,
    getPlayerMegaTickets,
    getUserMegaTicketIds,
    getMegaJackpotInfo,
    getLevelPriceNano,
} from "../blockchain/lotteryClient";
import { Spin } from "antd";
import { fromNano } from "@ton/core";

type TicketKind = "regular" | "mega";

type UserTicket = {
    ticket_number: string;
    is_active: boolean;
    wallet_address: string;
    level: number;
    cycleId: number;
    price: number;
    kind: TicketKind;
};

type TicketsBucket = {
    jackpot: UserTicket[];
    mega: UserTicket[];
};

type TicketCounts = {
    jackpot: number;
    mega: number;
};

const toSafeNumber = (value: unknown, fallback = 0): number => {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : fallback;
    }

    if (typeof value === "bigint") {
        return Number(value);
    }

    if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    return fallback;
};

const LEVEL_BY_INDEX: Record<number, number> = {
    0: 0,
    1: 1,
    2: 2,
};

function Profile() {
    const [isActive, setIsActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ticketsByLevel, setTicketsByLevel] = useState<
        Record<number, TicketsBucket>
    >({
        0: { jackpot: [], mega: [] },
        1: { jackpot: [], mega: [] },
        2: { jackpot: [], mega: [] },
    });

    const [ticketCounts, setTicketCounts] = useState<
        Record<number, TicketCounts>
    >({
        0: { jackpot: 0, mega: 0 },
        1: { jackpot: 0, mega: 0 },
        2: { jackpot: 0, mega: 0 },
    });

    const [centerIndex, setCenterIndex] = useState(0);

    const address = useTonAddress();
    const currentLevel = LEVEL_BY_INDEX[centerIndex];

    // =========================
    // LOAD DATA
    // =========================
    useEffect(() => {
        if (!address) return;

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const nextTicketsByLevel: Record<number, TicketsBucket> = {
                    0: { jackpot: [], mega: [] },
                    1: { jackpot: [], mega: [] },
                    2: { jackpot: [], mega: [] },
                };

                const nextCounts: Record<number, TicketCounts> = {
                    0: { jackpot: 0, mega: 0 },
                    1: { jackpot: 0, mega: 0 },
                    2: { jackpot: 0, mega: 0 },
                };

                const [megaTicketsInfo, megaTicketIds, megaInfo] = await Promise.all([
                    getPlayerMegaTickets(address),
                    getUserMegaTicketIds(address),
                    getMegaJackpotInfo(),
                ]);

                const megaCount = toSafeNumber(megaTicketsInfo?.count, 0);
                const megaCycleId = toSafeNumber(megaTicketsInfo?.cycleId, 0);
                const megaPrice = megaInfo?.ticketPrice
                    ? Number(fromNano(megaInfo.ticketPrice.toString()))
                    : 0;

                const megaTickets = Array.from({ length: megaCount }, (_, i) => {
                    const ticketId = megaTicketIds[i] ?? i + 1;

                    return {
                        ticket_number: `${megaCycleId}-${ticketId}`,
                        is_active: true,
                        wallet_address: address,
                        level: 999,
                        cycleId: megaCycleId,
                        price: megaPrice,
                        kind: "mega" as const,
                    } satisfies UserTicket;
                });

                for (let level = 0; level <= 2; level++) {
                    const [ticketsInfo, cycle, levelPriceNano] = await Promise.all([
                        getPlayerTicketsInLevel(address, level),
                        getCycle(level),
                        getLevelPriceNano(level),
                    ]);

                    if (!ticketsInfo || cancelled) continue;

                    const count = toSafeNumber(ticketsInfo.count, 0);
                    const playerCycleId = toSafeNumber(ticketsInfo.cycleId, 0);
                    const currentCycleId = toSafeNumber(cycle?.id, 0);

                    const price = Number(fromNano(levelPriceNano ?? 0n));

                    const isActiveCycle = Boolean(
                        playerCycleId === currentCycleId && cycle && !cycle.isFinished
                    );

                    const tickets = Array.from({ length: count }, (_, i) => ({
                        ticket_number: `${playerCycleId}-${i + 1}`,
                        is_active: isActiveCycle,
                        wallet_address: address,
                        level,
                        cycleId: playerCycleId,
                        price,
                        kind: "regular" as const,
                    }));

                    nextTicketsByLevel[level] = { jackpot: tickets, mega: megaTickets };
                    nextCounts[level] = { jackpot: count, mega: megaTickets.length };
                }

                if (cancelled) return;

                setTicketsByLevel(nextTicketsByLevel);
                setTicketCounts(nextCounts);
            } catch (e) {
                console.error("Failed to load tickets", e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [address]);

    // =========================
    // DERIVED DATA
    // =========================
    const currentBucket = ticketsByLevel[currentLevel];

    const currentTickets = useMemo(() => {
        return isActive
            ? currentBucket.mega
            : currentBucket.jackpot;
    }, [currentBucket, isActive]);

    const activeTickets = useMemo(() => {
        return currentTickets.filter(t => t.is_active);
    }, [currentTickets]);

    const inActiveTickets = useMemo(() => {
        return currentTickets.filter(t => !t.is_active);
    }, [currentTickets]);

    // =========================
    // HANDLERS
    // =========================
    const handleClickJackpot = () => {
        setIsActive(false);
    };

    const handleClickMegaJackpot = () => {
        setIsActive(true);
    };


    const carouselItems = useMemo(() => {
        return touchItems.map((item, idx) => {
            const level = LEVEL_BY_INDEX[idx];
            const jackpot = ticketCounts[level]?.jackpot ?? 0;
            const mega = ticketCounts[level]?.mega ?? 0;
            const total = jackpot + mega;

            return {
                ...item,
                subtitle: `${total} билетов`,
            };
        });
    }, [ticketCounts]);

    return (
        <>
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(11, 20, 48, 0.85)",
                        backdropFilter: "blur(4px)",
                    }}
                >
                    <Spin size="large" tip="Загрузка билетов..." />
                </div>
            )}

            <div className="profile">
                <div className="profile_header">
                    Мои билеты
                </div>

                <ThreeDCarousel
                    items={carouselItems}
                    centerIndex={centerIndex}
                    setCenterIndex={setCenterIndex}
                />

                <div className="profile_switch-btn-group">
                    <div
                        className={isActive ? "profile_switch-btn-group__btn" : "profile_switch-btn-group__btn is-active-btn"}
                        onClick={handleClickJackpot}
                    >
                        <span
                            className={isActive ? "profile_switch-btn-group__btn___content" : "profile_switch-btn-group__btn___content is-active-btn"}
                        >
                            Джекпот {ticketCounts[currentLevel].jackpot}
                        </span>
                    </div>

                    <div
                        className={!isActive ? "profile_switch-btn-group__btn" : "profile_switch-btn-group__btn is-active-btn"}
                        onClick={handleClickMegaJackpot}
                    >
                        <span
                            className={!isActive ? "profile_switch-btn-group__btn___content" : "profile_switch-btn-group__btn___content is-active-btn"}
                        >
                            Мега Джекпот {ticketCounts[currentLevel].mega}
                        </span>
                    </div>
                </div>

                <div className="active-tickets">
                    <div className="active-tickets_header">
                        Активные билеты
                    </div>

                    {activeTickets.map((ticket: UserTicket) => (
                        <Ticket key={ticket.ticket_number} ticket={ticket} />
                    ))}

                    <div className="active-tickets_header">
                        Предыдущие билеты
                    </div>

                    {inActiveTickets.map((ticket: UserTicket) => (
                        <Ticket key={ticket.ticket_number} ticket={ticket} />
                    ))}
                </div>
            </div>
        </>
    );

}

export default Profile;